import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import Logger from '../services/logger';
import { useAuth } from './AuthContext';
import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { doc, getDoc } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS } from '../constants/firestoreCollections';

interface LocationContextType {
  location: [number, number] | null;
  error: string | null;
  permissionState: 'prompt' | 'granted' | 'denied' | 'unknown';
  requestLocation: () => void;
  retry: () => void;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

// Geolocation error codes
const GEOLOCATION_ERROR = {
  PERMISSION_DENIED: 1,
  POSITION_UNAVAILABLE: 2,
  TIMEOUT: 3
} as const;

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [location, setLocation] = useState<[number, number] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  const isRequestingRef = useRef(false);
  const retryAttemptRef = useRef(0);
  const requestLocationInternalRef = useRef<((useHighAccuracy: boolean, extendedTimeout: boolean, forceRetry?: boolean) => void) | null>(null);
  const cachedLocationFetchedRef = useRef(false);
  const { user, isAuthenticated } = useAuth();

  const checkPermission = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' as PermissionName })
        .then((result) => {
          setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
          // Listen for permission changes
          result.onchange = () => {
            setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
          };
        })
        .catch((err) => {
          Logger.warn('[LocationContext] Permission query failed:', err);
          setPermissionState('unknown');
        });
    } else {
      setPermissionState('unknown');
    }
  }, []);

  const checkSecureContext = useCallback(() => {
    if (typeof window === 'undefined') return true;
    // Allow location when: HTTPS, localhost, 127.0.0.1, or IP (so mobile at http://IP:3000 can request location in dev)
    const isLocalOrIP = window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1' ||
                        /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(window.location.hostname);
    const isSecure = window.isSecureContext ||
                    window.location.protocol === 'https:' ||
                    isLocalOrIP;
    
    if (!isSecure) {
      Logger.error('[LocationContext] Not in secure context. Geolocation requires HTTPS or localhost.', new Error('Not in secure context'));
      setError('Geolocation requires a secure connection (HTTPS). Please access this site over HTTPS.');
      return false;
    }
    return true;
  }, []);

  const requestLocationInternal = useCallback((useHighAccuracy: boolean, extendedTimeout: boolean, forceRetry: boolean = false) => {
    // Prevent multiple simultaneous requests, but allow forced retries
    if (isRequestingRef.current && !forceRetry) {
      Logger.debug('[LocationContext] Location request already in progress, skipping...');
      return;
    }

    // If forcing a retry, reset the ref to allow the new request
    if (forceRetry && isRequestingRef.current) {
      Logger.debug('[LocationContext] Forcing retry, resetting request state...');
      isRequestingRef.current = false;
    }

    setError(null);
    
    // Check secure context
    if (!checkSecureContext()) {
      isRequestingRef.current = false; // Reset on early return
      return;
    }

    checkPermission();

    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      const errorMsg = 'Geolocation is not supported by your browser. Please use a modern browser with location services.';
      Logger.error('[LocationContext] Geolocation not supported', new Error(errorMsg));
      setError(errorMsg);
      setLocation(null);
      isRequestingRef.current = false; // Reset on early return
      return;
    }

    isRequestingRef.current = true;
    // PRODUCTION FIX: High accuracy GPS can take longer, especially indoors or with weak signal
    // Use longer timeout for high accuracy requests (30s) vs standard (20s)
    const timeout = useHighAccuracy 
      ? (extendedTimeout ? 30000 : 30000) // 30s for high accuracy GPS (allows time for GPS fix)
      : (extendedTimeout ? 20000 : 15000); // 20s for standard accuracy
    
    // Always log location requests for production debugging
    Logger.info(`[LocationContext] Requesting location (highAccuracy: ${useHighAccuracy}, timeout: ${timeout}ms, forceRetry: ${forceRetry})...`);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        isRequestingRef.current = false;
        retryAttemptRef.current = 0;
        const loc: [number, number] = [pos.coords.longitude, pos.coords.latitude];
        Logger.info('[LocationContext] Location obtained successfully:', {
          location: loc,
          accuracy: pos.coords.accuracy,
          timestamp: new Date().toISOString()
        });
        setLocation(loc);
        setError(null);
        checkPermission();
      },
      (err) => {
        // Always reset the ref in error handler to allow retries
        isRequestingRef.current = false;
        
        let errorMessage = 'Location access is required. Please allow location in your browser settings and click Retry.';
        
        // Handle specific error codes
        switch (err.code) {
          case GEOLOCATION_ERROR.PERMISSION_DENIED:
            errorMessage = 'Location access was denied. Please enable location permissions in your browser settings and click Retry.';
            Logger.error('[LocationContext] Permission denied by user', new Error('Permission denied'));
            setPermissionState('denied');
            break;
            
          case GEOLOCATION_ERROR.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable. Please check your device location settings and try again.';
            Logger.error('[LocationContext] Position unavailable:', new Error(err.message));
            break;
            
          case GEOLOCATION_ERROR.TIMEOUT:
            Logger.warn('[LocationContext] Location request timed out', {
              timeout,
              useHighAccuracy,
              message: 'GPS may need more time or better signal. Try moving to an area with better GPS reception or near a window.'
            });
            // PRODUCTION FIX: More helpful timeout message for high accuracy GPS
            errorMessage = useHighAccuracy
              ? 'Location request timed out. High accuracy GPS may need more time or better signal. Try moving to an area with better GPS reception (near a window or outdoors) and click Retry.'
              : 'Location request timed out. Please ensure location services are enabled and try again.';
            break;
            
          default:
            Logger.error('[LocationContext] Unknown geolocation error:', new Error(err.message || 'Unknown error'));
            errorMessage = `Location error: ${err.message || 'Unknown error'}. Please try again.`;
        }
        
        setError(errorMessage);
        setLocation(null);
        checkPermission();
      },
      { 
        enableHighAccuracy: useHighAccuracy,
        timeout: timeout,
        maximumAge: 60000 // PRODUCTION FIX: Allow browser-cached location up to 1 minute old (faster initial load)
      }
    );
  }, [checkPermission, checkSecureContext]);
  
  // Store latest requestLocationInternal in ref to avoid stale closures in useEffect
  useEffect(() => {
    requestLocationInternalRef.current = requestLocationInternal;
  }, [requestLocationInternal]);

  const requestLocation = useCallback(() => {
    requestLocationInternal(true, false);
  }, [requestLocationInternal]);

  const retry = useCallback(() => {
    Logger.info('[LocationContext] Retry requested - forcing new location request');
    retryAttemptRef.current = 0;
    setError(null);
    setLocation(null);
    // Reset the request ref to allow immediate retry
    isRequestingRef.current = false;
    
    // FIXED: Check permission state first to ensure we have latest state
    checkPermission();
    
    // PRODUCTION FIX: Test if geolocation is available before attempting
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      const errorMsg = 'Geolocation is not supported by your browser. Please use a modern browser with location services.';
      Logger.error('[LocationContext] Geolocation not supported on retry', new Error(errorMsg));
      setError(errorMsg);
      return;
    }
    
    // Check secure context
    if (!checkSecureContext()) {
      Logger.error('[LocationContext] Not in secure context on retry', new Error('Not in secure context'));
      setError('Geolocation requires a secure connection (HTTPS). Please access this site over HTTPS.');
      return;
    }
    
    // Immediately force retry - no delay to ensure prompt shows
    // This is called from user interaction (button click), so browser will show prompt
    Logger.info('[LocationContext] Executing retry with forceRetry=true immediately');
    requestLocationInternal(true, false, true); // Pass forceRetry=true to bypass debounce
  }, [requestLocationInternal, checkSecureContext, checkPermission]);

  // Track location and permission state in refs for use in event handlers
  const locationRef = useRef<[number, number] | null>(null);
  const permissionStateRef = useRef<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
  
  // Keep refs in sync with state
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  
  useEffect(() => {
    permissionStateRef.current = permissionState;
  }, [permissionState]);
  
  useEffect(() => {
    // FIXED: Check permission state on mount
    checkPermission();
    
    // FIXED: If permission is already granted, we can request location automatically
    // (No user interaction needed if permission was previously granted)
    // Use a small delay to ensure permission state is checked first
    const checkAndRequest = setTimeout(() => {
      if (permissionState === 'granted' && !location && !isRequestingRef.current && requestLocationInternalRef.current) {
        Logger.debug('[LocationContext] Permission already granted, requesting location automatically...');
        requestLocationInternalRef.current(true, false);
      }
    }, 100); // Reduced delay from 500ms to 100ms for faster response
    
    // Re-request on window focus only if permission is granted (prevents random popups)
    const onFocus = () => {
      // Use refs to check current state to avoid stale closure
      // Only re-request if:
      // 1. No location yet
      // 2. Not currently requesting
      // 3. Permission is granted (user already allowed)
      if (!locationRef.current && !isRequestingRef.current && permissionStateRef.current === 'granted') {
        Logger.debug('[LocationContext] Window focused, re-requesting location (permission granted)...');
        if (requestLocationInternalRef.current) {
          requestLocationInternalRef.current(true, false);
        }
      }
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus);
      return () => {
        clearTimeout(checkAndRequest);
        window.removeEventListener('focus', onFocus);
      };
    }
  }, [checkPermission, permissionState, location]); // Include permissionState and location to react to changes

  // PRODUCTION: Smart retry logic for signed-in users - only retry if permission is granted but location failed
  // This prevents random popups by only retrying when we know permission is available
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return; // Only for signed-in users
    }

    // Only retry if:
    // 1. Location is null
    // 2. We're not currently requesting
    // 3. Permission is granted (user already allowed, just need to retry)
    // 4. We haven't retried too many times recently
    if (!location && !isRequestingRef.current && permissionState === 'granted') {
      const retryKey = `location-retry-${user.uid}`;
      const lastRetry = sessionStorage.getItem(retryKey);
      const now = Date.now();
      
      // Only retry if last retry was more than 5 seconds ago (prevents rapid retries)
      if (!lastRetry || (now - parseInt(lastRetry)) > 5000) {
        sessionStorage.setItem(retryKey, now.toString());
        Logger.debug('[LocationContext] Retrying location for signed-in user with granted permission', { userId: user.uid });
        
        // Small delay to avoid immediate retry after mount
        const retryTimer = setTimeout(() => {
          if (requestLocationInternalRef.current && !isRequestingRef.current) {
            requestLocationInternalRef.current(true, false, true);
          }
        }, 2000); // Increased delay to 2 seconds to reduce popup frequency
        
        return () => clearTimeout(retryTimer);
      }
    }
  }, [isAuthenticated, user?.uid, location, permissionState]); // Include permissionState to react to permission changes

  // PRODUCTION FIX: Fetch cached location from Firestore on login for instant UI
  // This provides immediate location while fresh GPS is being obtained
  useEffect(() => {
    if (!isAuthenticated || !user || location || cachedLocationFetchedRef.current) {
      return; // Only fetch if authenticated, user exists, no location yet, and not already fetched
    }

    const fetchCachedLocation = async () => {
      cachedLocationFetchedRef.current = true; // Mark as fetched to prevent duplicate calls
      let foundCachedLocation = false;

      try {
        const db = getFailoverAwareDatabase();
        
        // Try 1: companyUserLive (Phase 1), then legacy companyUsers fields
        try {
          const liveDoc = await getDoc(doc(db, FIRESTORE_COLLECTIONS.COMPANY_USER_LIVE, user.uid));
          if (liveDoc.exists()) {
            const liveData = liveDoc.data();
            const cachedLocation = liveData.lastLocation || liveData.location;
            if (Array.isArray(cachedLocation) && cachedLocation.length === 2) {
              const [lng, lat] = cachedLocation;
              if (typeof lng === 'number' && typeof lat === 'number' && !isNaN(lng) && !isNaN(lat)) {
                const cachedLoc: [number, number] = [lng, lat];
                Logger.info('[LocationContext] Using cached location from companyUserLive', { location: cachedLoc, userId: user.uid });
                setLocation(cachedLoc);
                foundCachedLocation = true;
              }
            }
          }
        } catch (err) {
          Logger.warn('[LocationContext] Error fetching cached location from companyUserLive:', err as Error);
        }

        if (!foundCachedLocation) {
          try {
            const userDoc = await getDoc(doc(db, FIRESTORE_COLLECTIONS.COMPANY_USERS, user.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const cachedLocation = userData.lastLocation || userData.location;

              if (Array.isArray(cachedLocation) && cachedLocation.length === 2) {
                const [lng, lat] = cachedLocation;
                if (typeof lng === 'number' && typeof lat === 'number' && !isNaN(lng) && !isNaN(lat)) {
                  const cachedLoc: [number, number] = [lng, lat];
                  Logger.info('[LocationContext] Using cached location from companyUsers', { location: cachedLoc, userId: user.uid });
                  setLocation(cachedLoc);
                  foundCachedLocation = true;
                }
              }
            }
          } catch (err) {
            Logger.warn('[LocationContext] Error fetching cached location from companyUsers:', err as Error);
          }
        }

        // Try 2: Fetch from locations collection (from locationService) if companyUsers didn't have it
        if (!foundCachedLocation) {
          try {
            const locationDoc = await getDoc(doc(db, 'locations', user.uid));
            if (locationDoc.exists()) {
              const locationData = locationDoc.data();
              let lng: number | undefined;
              let lat: number | undefined;
              
              if (Array.isArray(locationData.position) && locationData.position.length === 2) {
                lng = locationData.position[0];
                lat = locationData.position[1];
              } else if (locationData.lng !== undefined && locationData.lat !== undefined) {
                lng = locationData.lng;
                lat = locationData.lat;
              }
              
              if (lng !== undefined && lat !== undefined && typeof lng === 'number' && typeof lat === 'number' && !isNaN(lng) && !isNaN(lat)) {
                const cachedLoc: [number, number] = [lng, lat];
                Logger.info('[LocationContext] Using cached location from locations collection', { location: cachedLoc, userId: user.uid });
                setLocation(cachedLoc);
                foundCachedLocation = true;
              }
            }
          } catch (err) {
            Logger.warn('[LocationContext] Error fetching cached location from locations collection:', err as Error);
          }
        }

        // After setting cached location (or if none found), request fresh GPS in background
        // This ensures UI shows immediately while getting accurate location
        if (foundCachedLocation) {
          Logger.debug('[LocationContext] Cached location set, requesting fresh GPS in background...');
          // Small delay to ensure cached location is set first
          setTimeout(() => {
            if (requestLocationInternalRef.current && !isRequestingRef.current) {
              requestLocationInternalRef.current(true, false, true);
            }
          }, 500);
        } else {
          // No cached location found, request GPS normally
          Logger.debug('[LocationContext] No cached location found, requesting GPS...');
          if (requestLocationInternalRef.current && !isRequestingRef.current) {
            requestLocationInternalRef.current(true, false);
          }
        }
      } catch (error) {
        Logger.error('[LocationContext] Error fetching cached location:', error as Error);
        // If cached location fetch fails, still request GPS
        if (requestLocationInternalRef.current && !isRequestingRef.current) {
          requestLocationInternalRef.current(true, false);
        }
      }
    };

    fetchCachedLocation();
  }, [isAuthenticated, user?.uid, location]); // Include location to reset ref when location changes

  // Reset cached location fetch flag when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      cachedLocationFetchedRef.current = false;
    }
  }, [isAuthenticated]);

  // Periodic location refresh for signed-in users (every 5 minutes)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    const refreshInterval = setInterval(() => {
      if (!isRequestingRef.current && requestLocationInternalRef.current) {
        // Reduced logging - periodic refresh doesn't need debug log every time
        requestLocationInternalRef.current(true, false, true);
      }
    }, 300000); // 5 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, user?.uid]); // Removed requestLocationInternal from deps to prevent loop

  return (
    <LocationContext.Provider value={{ location, error, permissionState, requestLocation, retry }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = () => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocationContext must be used within a LocationProvider');
  return ctx;
}; 